import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { originToReturnTo } from '~/lib/chat-origin';
import { useCodingOpenStore } from '~/lib/stores/coding-open-store';

/**
 * Opens the building (coding-session) window from anywhere in the app.
 *
 * The window itself only ever mounts on Home — `home.tsx` is the single
 * consumer of `useCodingOpenStore`'s open request — so reaching it from
 * another page means navigating there first and handing the origin along, so
 * closing the window returns the person to where they came from. That is the
 * mechanism FRONT-1550 established for a build row in the activity board;
 * this hook is the ONE definition of it, shared by every caller.
 *
 * `sessionId` defaults to `null`, meaning "no particular session": the window
 * resumes the most recent build, or lands on the name-your-project step if
 * there has never been one. Pass an id to attach to a specific build.
 *
 * Two surfaces call it — the top bar's Build button and, in the legacy
 * classic chat design, the header's own compact one. They must not each
 * re-derive the navigate-then-request dance: the earlier version had two
 * near-identical copies in `chat-top-bar.tsx` alone.
 */
export function useOpenBuild(): (sessionId?: string | null) => void {
  const location = useLocation();
  const navigate = useNavigate();
  const requestOpenCoding = useCodingOpenStore((s) => s.requestOpenCoding);

  return useCallback(
    (sessionId: string | null = null) => {
      const returnTo = originToReturnTo(location);
      if (returnTo !== null) navigate('/');
      requestOpenCoding(sessionId, returnTo);
    },
    [location, navigate, requestOpenCoding],
  );
}
